import { get, map } from 'lodash';
import { Grid, TextInput, RadioButtonGroup, Select, Typography, Checkbox } from '@ts/storybook';
import { useState, useEffect } from 'react';
import NeedsManager from '@utils/NeedsManager';
import { WrapperEntitySelector } from '@components/Inputs';
import Misc, { Loading } from '@components/Misc';
import NumberValuesDefinition from './NumberValuesDefinition';
import ListValuesDefinition from './ListValuesDefinition';
import IconRulesCrud from './IconRulesCrud';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { useTranslation } from 'react-i18next';
import { CStatusIndicator, IForm } from '@ts/ui-models';
import { IAppProps } from '@interfaces/App.interface';

const types = {
  list: {
    display: 'Values from predefined list',
    valuesDefinition: ListValuesDefinition,
  },
  integer: { display: 'Integers', valuesDefinition: NumberValuesDefinition },
  float: { display: 'Floats', valuesDefinition: NumberValuesDefinition },
  boolean: { display: 'Boolean', valuesDefinition: null, defaultValues: [true, false] },
  string: { display: 'Free value', valuesDefinition: null },
  timestamp: { display: 'Date and time', valuesDefinition: null },
  date: { display: 'Date', valuesDefinition: null },
  time: { display: 'Time', valuesDefinition: null },
  distance: { display: 'Last travel distance', valuesDefinition: null },
  speed: { display: 'Last travel speed', valuesDefinition: null },
};

const cellDisplays = {
  none: 'Do not display',
  text: 'Text value',
  icon: 'Icon ',
};

interface IStatusIndicatorFormProps extends IForm, IAppProps {
  values: CStatusIndicator;
}

const StatusIndicatorForm = (props: IStatusIndicatorFormProps) => {
  let { values, requiredFields, mode, onCallbackChange } = props;
  const { t } = useTranslation();

  const [liveUpdateReadingType, setLiveUpdateReadingType] = useState(values?.readingType);

  useEffect(() => {
    let needs = [
      {
        url: '/api/readingTypes',
        dataPath: 'locationReadingTypes',
        data: {
          where: {
            $or: [{ logicalId: 'locationLocalCoordinates' }, { logicalId: 'locationGPSLatLonCoordinates' }],
          },
        },
      },
      { url: '/api/units', dataPath: 'units' },
    ];
    let needsManager = new NeedsManager(props.data, needs);
    needsManager.fetchNeeds();
  }, []);

  const readingTypes = props.data.getIn(['locationReadingTypes', 'data']);
  const allUnits = props.data.getIn(['units', 'data']) && props.data.getIn(['units', 'data']).toJS();
  if (!readingTypes || !allUnits) return <Loading />;

  const statusReadingTypeUnit = liveUpdateReadingType && allUnits.find(unit => unit.logicalId === liveUpdateReadingType.unitId);

  const units = statusReadingTypeUnit
    ? allUnits.filter(unit => unit.quantityId === statusReadingTypeUnit.quantityId)
    : liveUpdateReadingType
    ? []
    : allUnits;

  const renderValuesDefinition = () => {
    const ValuesDefinition = types[values.type].valuesDefinition;
    if (!ValuesDefinition) return <div />;
    const fixList = values.type === 'boolean';
    return <ValuesDefinition {...props} fixList={fixList} defaultValues={types[values.type]?.defaultValues ?? null} />;
  };
  const handleChangeType = value => {
    const type = value;
    if (type == 'speed' || type == 'distance') {
      //set live and disable update
      const update = values.update;
      onCallbackChange('update')({ ...update, live: true, manual: false });
    }
    onCallbackChange('type')(type);
  };

  const handleChangeReadingType = entity => {
    const value = entity ? entity.getID() : null;
    onCallbackChange('readingTypeId')(value);
    setLiveUpdateReadingType(entity);
    if (entity) {
      // console.log(allUnits)
      const readingTypeUnit = allUnits.find(unit => unit.logicalId === entity.unitId);
      if (readingTypeUnit) {
        return onCallbackChange('unitId')(readingTypeUnit.logicalId);
      }
    }
    return onCallbackChange('unitId')(null);
  };

  const onChangeUpdate = key => value => {
    const update = { ...values.update, [key]: value };
    onCallbackChange('update')(update);
    // unset readingTypeId if live is deselected
    if (key === 'live' && !value) onCallbackChange('readingTypeId')(null);
  };

  function renderUpdate() {
    const onChangeAnyReading = v => {
      const checked = v;
      if (checked) {
        onCallbackChange('readingTypeId')(null);
        onCallbackChange('unitId')(null);
      }
      onChangeUpdate('anyReading')(v);
    };
    const renderAnyReading = () => {
      if (!values.update || !values.update.live || values.type != 'timestamp') return;
      return (
        <Checkbox
          disabled={isDistanceOrSpeed}
          isReadOnly={isViewMode}
          checked={values.update.anyReading ?? false}
          onChangeValue={onChangeAnyReading}
          label="Update for any reading"
        />
      );
    };

    const enabledReadingTypeSelector = values.update && values.update.live && !values.update.anyReading && !isDistanceOrSpeed;

    return (
      <>
        <Grid size={{ xs: 12 }}>
          <Checkbox
            label="Allow manual updates"
            disabled={isDistanceOrSpeed}
            isReadOnly={isViewMode}
            checked={values.update?.manual || false}
            onChangeValue={onChangeUpdate('manual')}
            stProps={{
              tooltip: {
                text: 'Enable this option to allow users to manually change the status indicators value via the UI, smartphone app or API',
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Checkbox
            label="Enabled automatic updates from live readings"
            disabled={isDistanceOrSpeed}
            isReadOnly={isViewMode}
            checked={values.update?.live || false}
            onChangeValue={onChangeUpdate('live')}
            stProps={{
              tooltip: {
                text: 'Enable this option to automatically update the status indicator based on incoming live readings mapped to this indicator type',
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 6 }} sx={{ pt: '5px!important' }}>
          {renderAnyReading()}
          {/* error={
                values.update && values.update.live && requiredFields.includes('readingTypeId') && !values.readingTypeId
                  ? true
                  : false
              } */}
          {enabledReadingTypeSelector && (
            <WrapperEntitySelector
              label={'Reading Type'}
              searchComponent={generateSearchEntitySelector('readingType')}
              isReadOnly={isViewMode}
              value={values.readingType ?? values.readingTypeId}
              onChangeEntity={handleChangeReadingType}
            />
          )}
        </Grid>
      </>
    );
  }

  function renderSpecialType() {
    const handleChangeSpecialType = value => {
      const readingTypeLid = value;
      const readingType = readingTypes.find(rt => rt.get('logicalId') == readingTypeLid);
      if (readingType) {
        onCallbackChange('readingTypeId')(readingType.get('id'));
        onCallbackChange('readingType')(readingType);
      }
    };

    if (!isDistanceOrSpeed) return;
    let coordinatesType: string = 'locationGPSLatLonCoordinates';

    const readingTypeId = values.readingTypeId;
    if (readingTypeId) {
      const readingType = readingTypes.find(rt => rt.get('id') == readingTypeId);
      coordinatesType = readingType ? readingType.get('logicalId') : null;
    }

    return (
      <RadioButtonGroup
        title={'Type of coordinates'}
        value={coordinatesType}
        onChange={handleChangeSpecialType}
        options={[
          {
            value: 'locationGPSLatLonCoordinates',
            label: 'GPS coordinates',
          },
          {
            value: 'locationLocalCoordinates',
            label: 'Local coordinates',
          },
        ]}
      />
    );
  }

  const isViewMode = mode == 'view';
  const isDistanceOrSpeed = values.type === 'speed' || values.type === 'distance';
  const enabledUnitSelector = units.length > 0 && !get(values, 'update.anyReading');

  return (
    <Grid container size={{ xs: 12 }} columnSpacing={1} rowSpacing={3} id="general-tab" pb={2} mt={2}>
      <Grid size={{ xs: 12, md: 12, lg: 6 }}>
        <Misc.NewPaperWithPadding>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" text="Main information" />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextInput
                isReadOnly={isViewMode}
                label="Name"
                value={values.name}
                onChangeValue={onCallbackChange('name')}
                required={requiredFields.includes('name')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Select
                label="Display"
                isReadOnly={isViewMode}
                value={values.display || ''}
                onChange={onCallbackChange('display')}
                options={map(cellDisplays, (v, k) => ({ label: t(v), value: k }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Select
                label="Type"
                isReadOnly={isViewMode}
                value={values.type}
                onChange={handleChangeType}
                options={map(types, (v, k) => ({ label: t(v.display), value: k }))}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>{renderSpecialType()}</Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" text="Values" />
            </Grid>
            {!['boolean', 'list'].includes(values.type) && (
              <Grid size={{ xs: 12 }}>
                <Select
                  label="Units"
                  isReadOnly={isViewMode || !enabledUnitSelector}
                  value={values.unitId || ''}
                  onChange={onCallbackChange('unitId')}
                  options={map(units, (v, k) => ({ label: v.name, value: v.logicalId }))}
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Checkbox
                label="Allow empty value"
                isReadOnly={isViewMode}
                checked={values.allowEmptyValue}
                onChangeValue={onCallbackChange('allowEmptyValue')}
                stProps={{
                  tooltip: {
                    text: 'Enable this option to permit the status indicator to have no value assigned',
                  },
                }}
              />
            </Grid>
            {renderUpdate()}
            <Grid size={{ xs: 12 }}>{renderValuesDefinition()}</Grid>
          </Grid>
        </Misc.NewPaperWithPadding>
      </Grid>
      {values.display === 'icon' && (
        <Grid size={{ xs: 12, md: 12, lg: 6 }}>
          <Misc.NewPaperWithPadding>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid size={{ xs: 12 }}>
                <IconRulesCrud {...props} />
              </Grid>
            </Grid>
          </Misc.NewPaperWithPadding>
        </Grid>
      )}
    </Grid>
  );
};

export default StatusIndicatorForm;
